import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ClipboardList, Calendar, MapPin, User, Trash2, Camera, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Insulation Started': '#3b82f6',
  'Insulation Complete': '#10b981',
  'Cladding Started': '#8b5cf6',
  'Cladding Complete': '#06b6d4',
  'Inspection Ready': '#f59e0b',
  'Touch Up Required': '#ef4444',
  'Complete': '#16a34a',
};

function getWeekRange(offset = 0) {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1) + offset * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    from: monday.toISOString().split('T')[0],
    to: sunday.toISOString().split('T')[0],
    label: `${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${sunday.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  };
}

export function ProjectProgressViewer({ projects, projectId, canDelete, embedded }) {
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [filterMode, setFilterMode] = useState('week');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [viewerPhotos, setViewerPhotos] = useState([]);
  const [viewerIdx, setViewerIdx] = useState(0);

  const weekRange = getWeekRange(weekOffset);

  useEffect(() => {
    if (projectId) setSelectedProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    if (selectedProjectId) fetchProgress();
  }, [selectedProjectId, weekOffset, filterMode, dateFrom, dateTo]);

  const fetchProgress = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    let url = `${API_URL}/api/projects/${selectedProjectId}/progress`;
    const params = new URLSearchParams();
    if (filterMode === 'week') {
      params.set('date_from', weekRange.from);
      params.set('date_to', weekRange.to);
    } else if (filterMode === 'custom' && dateFrom) {
      params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
    }
    if (params.toString()) url += `?${params.toString()}`;

    try {
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        setEntries(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch progress:', e);
    }
    setLoading(false);
  };

  const handleDelete = async (progressId) => {
    if (!window.confirm('Delete this progress entry?')) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/projects/${selectedProjectId}/progress/${progressId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Progress entry deleted');
        setEntries(prev => prev.filter(e => e.id !== progressId));
      } else {
        toast.error('Failed to delete');
      }
    } catch {
      toast.error('Connection error');
    }
  };

  const openPhotoViewer = (photos, startIdx) => {
    setViewerPhotos(photos);
    setViewerIdx(startIdx);
    setPhotoViewerOpen(true);
  };

  const selectedProject = projects?.find(p => p.id === selectedProjectId);

  return (
    <div data-testid="project-progress-viewer">
      {/* Project Selector (hidden if embedded in project details) */}
      {!embedded && (
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs text-gray-500 mb-1 block">Project</Label>
            <ProjectCombobox
              projects={projects || []}
              value={selectedProjectId}
              onChange={setSelectedProjectId}
              placeholder="Select a project"
              filterActive={false}
              testId="progress-project"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Filter</Label>
            <Select value={filterMode} onValueChange={setFilterMode}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">By Week</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filterMode === 'week' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w - 1)} data-testid="progress-prev-week">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium whitespace-nowrap min-w-[180px] text-center">{weekRange.label}</span>
              <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0} data-testid="progress-next-week">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          {filterMode === 'custom' && (
            <div className="flex items-center gap-2">
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" />
              <span className="text-xs text-gray-400">to</span>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" />
            </div>
          )}
        </div>
      )}

      {/* Week navigation for embedded mode */}
      {embedded && (
        <div className="flex items-center justify-between mb-3">
          <Select value={filterMode} onValueChange={setFilterMode}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">By Week</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          {filterMode === 'week' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-xs font-medium whitespace-nowrap">{weekRange.label}</span>
              <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {!selectedProjectId && !embedded && (
        <div className="text-center py-12 text-gray-400">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Select a project to view progress reports</p>
        </div>
      )}

      {selectedProjectId && loading && (
        <div className="text-center py-8 text-gray-400 text-sm">Loading progress...</div>
      )}

      {selectedProjectId && !loading && entries.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          <Camera className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No progress reports {filterMode === 'week' ? 'this week' : 'found'}</p>
        </div>
      )}

      {/* Summary stats */}
      {selectedProjectId && !loading && entries.length > 0 && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-center">
            <p className="text-lg font-bold text-blue-700">{entries.length}</p>
            <p className="text-xs text-blue-600">Reports</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-center">
            <p className="text-lg font-bold text-green-700">{entries.reduce((s, e) => s + (e.photos?.length || 0), 0)}</p>
            <p className="text-xs text-green-600">Photos</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-center">
            <p className="text-lg font-bold text-purple-700">{new Set(entries.map(e => e.reported_by)).size}</p>
            <p className="text-xs text-purple-600">Contributors</p>
          </div>
        </div>
      )}

      {/* Progress entries timeline */}
      <div className="space-y-3">
        {entries.map((entry) => (
          <div key={entry.id} className="border rounded-lg overflow-hidden bg-white" data-testid={`progress-entry-${entry.id}`}>
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge style={{ background: STATUS_COLORS[entry.work_status] || '#6b7280', color: 'white', border: 'none' }}>
                  {entry.work_status}
                </Badge>
                {entry.floor && <span className="text-xs text-gray-500">Floor: {entry.floor}</span>}
                {entry.unit_no && <span className="text-xs text-gray-500">Unit: {entry.unit_no}</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">
                  {new Date(entry.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  {' '}
                  {new Date(entry.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </span>
                {canDelete && (
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(entry.id)}>
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                )}
              </div>
            </div>
            <div className="px-4 py-3">
              <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                <User className="w-3 h-3" />
                <span>{entry.reported_by_name}</span>
                {entry.location?.address && (
                  <>
                    <span className="mx-1">|</span>
                    <MapPin className="w-3 h-3" />
                    <span className="truncate max-w-[200px]">{entry.location.address}</span>
                  </>
                )}
              </div>
              {entry.notes && <p className="text-sm text-gray-700 mb-2">{entry.notes}</p>}
              {entry.photos?.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {entry.photos.map((photo, idx) => (
                    <img
                      key={idx}
                      src={`${API_URL}${photo.url}`}
                      alt={photo.name || 'Progress'}
                      className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => openPhotoViewer(entry.photos, idx)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Photo Viewer Dialog */}
      <Dialog open={photoViewerOpen} onOpenChange={setPhotoViewerOpen}>
        <DialogContent className="max-w-3xl p-2">
          <DialogHeader>
            <DialogTitle className="text-sm">Photo {viewerIdx + 1} of {viewerPhotos.length}</DialogTitle>
          </DialogHeader>
          {viewerPhotos.length > 0 && (
            <div className="relative">
              <img
                src={`${API_URL}${viewerPhotos[viewerIdx]?.url}`}
                alt="Progress photo"
                className="w-full max-h-[70vh] object-contain rounded"
              />
              {viewerPhotos[viewerIdx]?.location?.address && (
                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                  <MapPin className="w-3 h-3" />{viewerPhotos[viewerIdx].location.address}
                </div>
              )}
              {viewerPhotos.length > 1 && (
                <div className="flex justify-between absolute top-1/2 -translate-y-1/2 w-full px-2">
                  <Button variant="secondary" size="icon" onClick={() => setViewerIdx(i => Math.max(0, i - 1))} disabled={viewerIdx === 0} className="rounded-full opacity-80">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button variant="secondary" size="icon" onClick={() => setViewerIdx(i => Math.min(viewerPhotos.length - 1, i + 1))} disabled={viewerIdx >= viewerPhotos.length - 1} className="rounded-full opacity-80">
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProjectProgressViewer;
